import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Mail, Trash2, Phone, User, Calendar, Inbox, Check, CheckCheck } from "lucide-react";

export default function ContactMessagesManager() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = () =>
    api.get("/admin/contact-messages")
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  useEffect(() => { refresh(); }, []);

  const view = async (m) => {
    setOpen(m);
    if (!m.read) {
      await api.patch(`/admin/contact-messages/${m.id}`, { read: true }).catch(() => {});
      refresh();
    }
  };

  const toggleRead = async (m, e) => {
    e.stopPropagation();
    await api.patch(`/admin/contact-messages/${m.id}`, { read: !m.read }).catch(() => {});
    refresh();
  };

  const remove = async (m, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm("Delete this message?")) return;
    await api.delete(`/admin/contact-messages/${m.id}`);
    if (open && open.id === m.id) setOpen(null);
    refresh();
  };

  const unread = items.filter((m) => !m.read).length;

  return (
    <section className="bg-white border border-slate-200 rounded-md p-5" data-testid="contact-messages-manager">
      <div className="flex items-center gap-2">
        <Mail className="h-5 w-5 text-nkf-red" />
        <h2 className="font-display text-xl text-nkf-blue">Contact Messages</h2>
        {unread > 0 && (
          <span className="ml-2 inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-nkf-red text-white text-[10px] font-bold">
            {unread}
          </span>
        )}
      </div>
      <p className="text-sm text-slate-600 mt-1">
        Messages submitted via the public <span className="font-semibold">/contact</span> form.
      </p>

      {loading ? (
        <div className="mt-6 text-sm text-slate-500">Loading…</div>
      ) : items.length === 0 ? (
        <div className="mt-8 py-12 border border-dashed border-slate-300 rounded-md flex flex-col items-center text-slate-400">
          <Inbox className="h-8 w-8 mb-2" />
          <div className="text-xs uppercase tracking-widest">No messages yet</div>
        </div>
      ) : (
        <ul className="mt-5 divide-y divide-slate-200 border border-slate-200 rounded-md overflow-hidden" data-testid="contact-messages-list">
          {items.map((m) => (
            <li
              key={m.id}
              data-testid={`contact-message-${m.id}`}
              onClick={() => view(m)}
              className={`px-4 py-3 cursor-pointer hover:bg-slate-50 transition flex items-start gap-3 ${m.read ? "" : "bg-amber-50/40"}`}
            >
              <div className={`mt-1 h-2 w-2 rounded-full ${m.read ? "bg-slate-300" : "bg-nkf-red"}`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2">
                  <div className={`text-sm truncate ${m.read ? "text-slate-700" : "font-bold text-nkf-blue"}`}>{m.name}</div>
                  <div className="text-xs text-slate-400 truncate">{m.email}</div>
                </div>
                <div className={`text-sm truncate ${m.read ? "text-slate-600" : "font-semibold text-slate-800"}`}>{m.subject}</div>
                <div className="text-xs text-slate-500 truncate mt-0.5">{m.message}</div>
              </div>
              <div className="flex flex-col items-end gap-1.5">
                <div className="text-[11px] text-slate-400 whitespace-nowrap">{formatDate(m.submitted_at)}</div>
                <div className="flex items-center gap-1">
                  <button
                    title={m.read ? "Mark as unread" : "Mark as read"}
                    onClick={(e) => toggleRead(m, e)}
                    data-testid={`contact-msg-toggle-${m.id}`}
                    className="p-1.5 hover:bg-slate-200 rounded text-slate-500"
                  >
                    {m.read ? <Check className="h-3.5 w-3.5" /> : <CheckCheck className="h-3.5 w-3.5" />}
                  </button>
                  <button
                    title="Delete"
                    onClick={(e) => remove(m, e)}
                    data-testid={`contact-msg-delete-${m.id}`}
                    className="p-1.5 hover:bg-red-100 hover:text-red-700 rounded text-slate-500"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {open && (
        <div
          className="fixed inset-0 bg-nkf-navy/60 z-50 flex items-center justify-center p-4"
          onClick={() => setOpen(null)}
          data-testid="contact-msg-modal"
        >
          <div
            className="bg-white rounded-md max-w-2xl w-full max-h-[88vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-nkf-blue text-white px-6 py-4 flex items-center justify-between">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-nkf-gold">Contact Message</div>
                <div className="font-display text-lg truncate">{open.subject}</div>
              </div>
              <button onClick={() => setOpen(null)} className="text-white/80 hover:text-white text-2xl leading-none">×</button>
            </div>
            <div className="p-6 space-y-4 text-sm">
              <Row icon={User}     label="From"        value={open.name} />
              <Row icon={Mail}     label="Email"       value={<a className="text-nkf-blue hover:underline" href={`mailto:${open.email}`}>{open.email}</a>} />
              {open.phone && <Row icon={Phone} label="Phone" value={<a className="text-nkf-blue hover:underline" href={`tel:${open.phone}`}>{open.phone}</a>} />}
              <Row icon={Calendar} label="Submitted"   value={formatDate(open.submitted_at)} />
              <div>
                <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-1.5 font-semibold">Message</div>
                <div className="bg-slate-50 border border-slate-200 rounded-md p-4 whitespace-pre-wrap text-slate-700">{open.message}</div>
              </div>
              <div className="pt-3 flex flex-wrap items-center justify-end gap-2 border-t border-slate-200">
                <a
                  href={`mailto:${open.email}?subject=Re: ${encodeURIComponent(open.subject)}`}
                  data-testid="contact-msg-reply"
                  className="inline-flex items-center gap-2 bg-nkf-blue text-white text-xs uppercase tracking-widest font-bold px-4 py-2 rounded hover:bg-nkf-navy"
                >
                  <Mail className="h-3.5 w-3.5" /> Reply by Email
                </a>
                <button
                  onClick={() => remove(open)}
                  data-testid="contact-msg-modal-delete"
                  className="inline-flex items-center gap-2 border border-red-300 text-red-700 text-xs uppercase tracking-widest font-bold px-4 py-2 rounded hover:bg-red-50"
                >
                  <Trash2 className="h-3.5 w-3.5" /> Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="h-4 w-4 mt-0.5 text-slate-400" />
      <div className="flex-1 min-w-0">
        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
        <div className="text-slate-800 break-words">{value}</div>
      </div>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}
